"use client";

import { c as _c } from "react/compiler-runtime";
import type { MouseEvent, TouchEvent } from "react";
import { useCallback, useRef, useState } from "react";
const DEFAULT_SWIPE_THRESHOLD = 25;
interface UseSwipeDismissArgs {
  readonly onDismiss: () => void;
  readonly threshold?: number | undefined;
}
interface SwipeDismissHandlers {
  readonly onTouchStart: (e: TouchEvent) => void;
  readonly onTouchMove: (e: TouchEvent) => void;
  readonly onTouchEnd: () => void;
  readonly onMouseDown: (e: MouseEvent) => void;
  readonly onMouseMove: (e: MouseEvent) => void;
  readonly onMouseUp: () => void;
  readonly onMouseLeave: () => void;
}
interface UseSwipeDismissResult {
  readonly swipeOffset: number;
  readonly swipeOpacity: number;
  readonly consumeSuppressClick: () => boolean;
  readonly handlers: SwipeDismissHandlers;
}
export function useSwipeDismiss(t0) {
  const $ = _c(23);
  const {
    onDismiss,
    threshold: t1
  } = t0;
  const threshold = t1 === undefined ? DEFAULT_SWIPE_THRESHOLD : t1;
  const [swipeOffset, setSwipeOffset] = useState(0);
  const swipeOffsetRef = useRef(0);
  const touchStartXRef = useRef(null);
  const isDraggingRef = useRef(false);
  const suppressClickRef = useRef(false);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      if (suppressClickRef.current) {
        suppressClickRef.current = false;
        return true;
      }
      return false;
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const consumeSuppressClick = t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      swipeOffsetRef.current = 0;
      setSwipeOffset(0);
      touchStartXRef.current = null;
    };
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const resetSwipe = t3;
  let t4;
  if ($[2] !== onDismiss || $[3] !== threshold) {
    t4 = () => {
      if (swipeOffsetRef.current >= threshold) {
        suppressClickRef.current = true;
        onDismiss();
      }
    };
    $[2] = onDismiss;
    $[3] = threshold;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const maybeDismiss = t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = e => {
      if (!e.touches[0]) {
        return;
      }
      touchStartXRef.current = e.touches[0].clientX;
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const handleTouchStart = t5;
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = e_0 => {
      if (touchStartXRef.current === null || !e_0.touches[0]) {
        return;
      }
      const deltaX = Math.max(0, e_0.touches[0].clientX - touchStartXRef.current);
      swipeOffsetRef.current = deltaX;
      setSwipeOffset(deltaX);
    };
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const handleTouchMove = t6;
  let t7;
  if ($[7] !== maybeDismiss) {
    t7 = () => {
      maybeDismiss();
      resetSwipe();
    };
    $[7] = maybeDismiss;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const handleTouchEnd = t7;
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = e_1 => {
      isDraggingRef.current = true;
      touchStartXRef.current = e_1.clientX;
    };
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const handleMouseDown = t8;
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = e_2 => {
      if (!isDraggingRef.current || touchStartXRef.current === null) {
        return;
      }
      const deltaX_0 = Math.max(0, e_2.clientX - touchStartXRef.current);
      swipeOffsetRef.current = deltaX_0;
      setSwipeOffset(deltaX_0);
    };
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  const handleMouseMove = t9;
  let t10;
  if ($[11] !== maybeDismiss) {
    t10 = () => {
      if (!isDraggingRef.current) {
        return;
      }
      maybeDismiss();
      resetSwipe();
      isDraggingRef.current = false;
    };
    $[11] = maybeDismiss;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  const handleMouseUp = t10;
  let t11;
  if ($[13] !== maybeDismiss) {
    t11 = () => {
      if (isDraggingRef.current) {
        maybeDismiss();
        resetSwipe();
        isDraggingRef.current = false;
      }
    };
    $[13] = maybeDismiss;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  const handleMouseLeave = t11;
  const t12 = Math.max(0, 1 - swipeOffset / threshold);
  let t13;
  if ($[15] !== handleMouseLeave || $[16] !== handleMouseUp || $[17] !== handleTouchEnd) {
    t13 = {
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd,
      onMouseDown: handleMouseDown,
      onMouseMove: handleMouseMove,
      onMouseUp: handleMouseUp,
      onMouseLeave: handleMouseLeave
    };
    $[15] = handleMouseLeave;
    $[16] = handleMouseUp;
    $[17] = handleTouchEnd;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== swipeOffset || $[20] !== t12 || $[21] !== t13) {
    t14 = {
      swipeOffset,
      swipeOpacity: t12,
      consumeSuppressClick,
      handlers: t13
    };
    $[19] = swipeOffset;
    $[20] = t12;
    $[21] = t13;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  return t14;
}